import React, { useState, useMemo } from 'react';
import Button from '../../components/Form/Button';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useRequisitions } from '../../hooks/useRequisitions';
import { useBranches } from '../../hooks/useBranches';
import { PageHeader, StatsCards, FilterBar, type FilterConfig, type StatCard } from '../../components/Common';

const RequisitionsReport: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [amountFrom, setAmountFrom] = useState<string>('');
  const [amountTo, setAmountTo] = useState<string>('');

  const { data: requisitionsData = [] } = useRequisitions();
  const { data: branchesData = [] } = useBranches();

  const filteredRequisitions = useMemo(() => {
    return requisitionsData.filter(requisition => {
      const matchesSearch = !searchTerm || (
        requisition.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        requisition.preparedBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
        requisition.vessel.toLowerCase().includes(searchTerm.toLowerCase())
      );

      const matchesStatus = statusFilter === 'all' || requisition.status === statusFilter;
      const matchesBranch = branchFilter === 'all' || requisition.department === branchFilter;

      const requisitionDate = new Date(requisition.requestDate);
      const matchesDateFrom = !dateFrom || requisitionDate >= new Date(dateFrom);
      const matchesDateTo = !dateTo || requisitionDate <= new Date(dateTo);

      const totalAmount = (requisition.items || []).reduce((sum, item) => sum + (item.unitPrice || 0) * item.qty, 0);
      const matchesAmountFrom = !amountFrom || totalAmount >= parseFloat(amountFrom);
      const matchesAmountTo = !amountTo || totalAmount <= parseFloat(amountTo);

      return matchesSearch && matchesStatus && matchesBranch && 
             matchesDateFrom && matchesDateTo && matchesAmountFrom && matchesAmountTo;
    });
  }, [searchTerm, statusFilter, branchFilter, dateFrom, dateTo, amountFrom, amountTo, requisitionsData]);

  const buildExportData = () => filteredRequisitions.map(requisition => {
    const branch = branchesData.find(b => b.name === requisition.department);
    const totalAmount = (requisition.items || []).reduce((sum, item) => sum + (item.unitPrice || 0) * item.qty, 0);
    return {
      'Requisition Number': requisition.id,
      'Prepared By': requisition.preparedBy,
      'Vessel': requisition.vessel,
      'Vessel/Headquarters': branch?.name || 'غير محدد',
      'Date': requisition.requestDate,
      'Total Amount (EGP)': totalAmount,
      'Status': requisition.status
    };
  });

  const handleExport = () => exportToExcel(buildExportData(), `requisitions_report_${new Date().toISOString().split('T')[0]}`);
  const handlePrint = () => printAsExcel(buildExportData(), 'Requisitions Report');

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setBranchFilter('all');
    setDateFrom('');
    setDateTo('');
    setAmountFrom('');
    setAmountTo('');
  };

  const totalAmount = useMemo(() => filteredRequisitions.reduce((acc, requisition) => {
    return acc + (requisition.items || []).reduce((sum, item) => sum + (item.unitPrice || 0) * item.qty, 0);
  }, 0), [filteredRequisitions]);

  // Requisition item prices are entered in EGP across the app
  const stats: StatCard[] = [
    { label: 'إجمالي الطلبات', value: filteredRequisitions.length, color: 'blue' },
    { label: 'إجمالي القيمة (جنيه)', value: `${totalAmount.toLocaleString()} EGP`, color: 'purple' },
    { label: 'متوسط الطلب (جنيه)', value: `${filteredRequisitions.length > 0 ? (totalAmount / filteredRequisitions.length).toFixed(0) : '0'} EGP`, color: 'green' },
    { label: 'الطلبات المشتراة', value: filteredRequisitions.filter(r => r.status === 'purchased').length, color: 'yellow' },
  ];

  const filters: FilterConfig[] = [
    { key: 'status', label: 'الحالة', type: 'select', value: statusFilter, onChange: setStatusFilter, options: [
      { value: 'all', label: 'جميع الحالات' }, { value: 'pending', label: 'قيد الانتظار' },
      { value: 'approved', label: 'معتمد' }, { value: 'purchased', label: 'مشترى' },
      { value: 'rejected', label: 'مرفوض' }, { value: 'cancelled', label: 'ملغي' },
    ]},
    { key: 'branch', label: 'الفرع', type: 'select', value: branchFilter, onChange: setBranchFilter, options: [
      { value: 'all', label: 'جميع الأقسام' },
      ...branchesData.map(branch => ({ value: branch.name, label: branch.name })),
    ]},
    { key: 'dateFrom', label: 'من تاريخ', type: 'date', value: dateFrom, onChange: setDateFrom },
    { key: 'dateTo', label: 'إلى تاريخ', type: 'date', value: dateTo, onChange: setDateTo },
    { key: 'amountFrom', label: 'من مبلغ', type: 'number', value: amountFrom, onChange: setAmountFrom, placeholder: '0' },
    { key: 'amountTo', label: 'إلى مبلغ', type: 'number', value: amountTo, onChange: setAmountTo, placeholder: '999999' },
  ];

  const statusMap: Record<string, { label: string; cls: string }> = {
    purchased: { label: 'مشترى', cls: 'bg-green-100 text-green-800' },
    approved: { label: 'معتمد', cls: 'bg-blue-100 text-blue-800' },
    pending: { label: 'قيد الانتظار', cls: 'bg-yellow-100 text-yellow-800' },
    rejected: { label: 'مرفوض', cls: 'bg-red-100 text-red-800' },
    cancelled: { label: 'ملغي', cls: 'bg-gray-100 text-gray-800' },
  };

  return (
    <div className="space-y-6" dir="rtl">
      <PageHeader title="تقرير طلبات التوريد" description="عرض وتحليل جميع طلبات التوريد مع إمكانية التصفية والتصدير" />
      <StatsCards stats={stats} />
      <FilterBar
        searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="البحث في طلبات التوريد..."
        filters={filters} onClearFilters={handleClearFilters}
        totalCount={requisitionsData.length} filteredCount={filteredRequisitions.length} entityName="تصفية التقرير"
        onExport={handleExport} onPrint={handlePrint}
      />

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              نتائج التقرير ({filteredRequisitions.length} طلب)
            </h3>
            <Button onClick={handleExport} variant="secondary" size="sm">تصدير CSV</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['رقم الطلب', 'مقدم الطلب', 'السفينة', 'الفرع', 'تاريخ الطلب', 'القيمة الإجمالية', 'الحالة'].map(h => (
                    <th key={h} className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRequisitions.map((requisition) => {
                  const branch = branchesData.find(b => b.name === requisition.department);
                  const reqTotal = (requisition.items || []).reduce((sum, item) => sum + (item.unitPrice || 0) * item.qty, 0);
                  const st = statusMap[requisition.status] || { label: requisition.status, cls: 'bg-gray-100 text-gray-800' };
                  return (
                    <tr key={requisition.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">{requisition.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{requisition.preparedBy}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{requisition.vessel}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{branch?.name || requisition.department || 'غير محدد'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{new Date(requisition.requestDate).toLocaleDateString('ar-EG')}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{reqTotal.toLocaleString()} جنيه</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredRequisitions.length === 0 && (
            <div className="text-center py-8"><p className="text-gray-500">لا توجد طلبات توريد تطابق المعايير المحددة</p></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequisitionsReport;
